import { Component, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { BackendService } from '../../../../core/backend.service';
import { CONFIG } from '../../../../core/config';
import { ModelCategoryCalibration } from '../../../../viewmodels/admin/model-category-calibration/model-visual-category-calibration';
import { RiskMatrix } from '../../../../viewmodels/admin/risk-matrices/risk-matrix';
import { RiskMatrixCell } from '../../../../viewmodels/admin/risk-matrices/risk-matrix-cell';
import { MatrixCalibration } from '../../../../viewmodels/analysis/matrix-calibration';
import { CellClickArgs } from '../../../risk-matrices/risk-matrix-grid.component';
import { ConsequenceRiskMatrixService } from './consequence-risk-matrix.service';
import { I18NextPipe } from 'angular-i18next';

@Component({
  selector: 'hb-consequence-risk-matrix-selection-dialog',
  templateUrl: './consequence-risk-matrix-selection-dialog.component.html',
  styleUrls: ['./consequence-risk-matrix-selection-dialog.component.scss'],
})
export class ConsequenceRiskMatrixSelectionDialogComponent implements OnInit, OnDestroy {
  public title: string;
  public selectedRiskMatrixId: string;
  public selectedRiskMatrixCell: RiskMatrixCell;
  public selectedRiskMatrix: RiskMatrix;
  public matrixCalibration: MatrixCalibration = new MatrixCalibration();
  public riskMatrices: RiskMatrix[];
  public selectedRiskRanking: string;

  private unsubscribe: Subject<boolean> = new Subject();
  selectedRiskMatrixCellCalibrationConsequence: ModelCategoryCalibration;
  selectedRiskMatrixCellCalibrationProbability: ModelCategoryCalibration;

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private riskMatrixService: ConsequenceRiskMatrixService,
    private backendService: BackendService,
    private i18NextPipe: I18NextPipe
  ) {}

  ngOnInit() {
    this.title = this.i18NextPipe.transform('ConsequenceRiskMatrixSelectionDialog.RiskMatrix');

    this.backendService
      .getAllRoute<RiskMatrix>(RiskMatrix, CONFIG.apiRoutes.calibratedRiskMatrices).pipe(
      takeUntil(this.unsubscribe))
      .subscribe(riskMatrices => {
        if (riskMatrices) {
          this.riskMatrices = riskMatrices;
        }
      });

    this.riskMatrixService.riskMatrixSelection.pipe(takeUntil(this.unsubscribe)).subscribe(riskMatrixSelection => {
      this.selectedRiskMatrixId = riskMatrixSelection.riskMatrixId;
      this.selectedRiskMatrixCell = riskMatrixSelection.riskMatrixCell;

      this.backendService
        .getById<RiskMatrix>(RiskMatrix, this.selectedRiskMatrixId).pipe(
        take(1))
        .subscribe(riskMatrix => {
          this.selectedRiskMatrix = riskMatrix;

          if (this.selectedRiskMatrix) {
            Array.from(this.selectedRiskMatrix.probabilityVectors).forEach(currentProbabilityVector => {
              const foundCellIndex = currentProbabilityVector.cells.findIndex(
                currentCell => currentCell.riskMatrixCellId === this.selectedRiskMatrixCell.riskMatrixCellId,
              );

              if (foundCellIndex !== -1) {
                this.updateRiskMatrixCalibration(currentProbabilityVector.cells[foundCellIndex]);
              }
            });
          }
        });
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  onSelectedMatrixChanged() {
    if (this.selectedRiskMatrixId) {
      this.backendService
        .getById<RiskMatrix>(RiskMatrix, this.selectedRiskMatrixId).pipe(
        take(1))
        .subscribe(riskMatrix => {
          if (riskMatrix) {
            this.selectedRiskMatrix = riskMatrix;
            this.selectedRiskMatrixCell = undefined;
            this.matrixCalibration.riskMatrixProbabilityVectorId = '';
            this.matrixCalibration.riskMatrixConsequenceVectorId = '';
          }
        });
    }
  }

  onCellClicked(event: CellClickArgs) {
    this.selectedRiskRanking = event.cell.riskBand.name;
    this.selectedRiskMatrixCell = event.cell;

    this.updateRiskMatrixCalibration(event.cell);
  }

  isValid(): boolean {
    if (this.selectedRiskMatrix && this.selectedRiskMatrixCell) {
      return true;
    }

    return false;
  }

  updateRiskMatrixCalibration(selectedCell: RiskMatrixCell) {
    if (this.selectedRiskMatrix.defaultCalibrations) {
      this.selectedRiskMatrixCellCalibrationConsequence = this.selectedRiskMatrix.defaultCalibrations.modelCategoryCalibration.find(
        currentCalibration => currentCalibration.riskMatrixConsequenceVectorId === this.selectedRiskMatrixCell.riskMatrixConsequenceVectorId,
      );

      this.selectedRiskMatrixCellCalibrationProbability = this.selectedRiskMatrix.defaultCalibrations.modelCategoryCalibration.find(
        currentCalibration => currentCalibration.riskMatrixProbabilityVectorId === this.selectedRiskMatrixCell.riskMatrixProbabilityVectorId,
      );
    }

    this.matrixCalibration.riskMatrixConsequenceVectorId = selectedCell.riskMatrixConsequenceVectorId;
    this.matrixCalibration.riskMatrixProbabilityVectorId = selectedCell.riskMatrixProbabilityVectorId;

    this.selectedRiskRanking = selectedCell.riskBand.name;
  }

  okClick() {
    this.riskMatrixService.acceptRiskMatrixSelection(this.selectedRiskMatrixId, this.selectedRiskMatrixCell);
  }
}
